import { useEffect, useState } from "react";
import { Trash2, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";

export const ManageVideoDialog = ({ open, onClose, video, onSaved, onDeleted }) => {
  const [genres, setGenres] = useState([]);
  const [form, setForm] = useState({ title: "", genre: "", description: "", thumbnail: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && video) {
      setForm({
        title: video.title || "",
        genre: video.genre || "",
        description: video.description || "",
        thumbnail: video.thumbnail || "",
      });
      setConfirmDelete(false);
      api.get("/genres").then((r) => setGenres(r.data)).catch(() => {});
    }
  }, [open, video]);

  if (!video) return null;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/videos/${video.id}`, form);
      toast.success("Video updated");
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/videos/${video.id}`);
      toast.success("Video deleted");
      onDeleted && onDeleted(video.id);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-zinc-900/60 border border-white/10 text-white placeholder-zinc-500 px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD60A]";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white rounded-none max-w-md" data-testid="manage-video-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Edit video</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Title</label>
            <input data-testid="edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Genre</label>
            <select data-testid="edit-genre" value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} className={`${inputCls} mt-1.5`}>
              {genres.map((g) => <option key={g} value={g} className="bg-zinc-900">{g}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Description</label>
            <textarea data-testid="edit-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} mt-1.5 resize-none`} />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400">Thumbnail URL</label>
            <input data-testid="edit-thumbnail" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} className={`${inputCls} mt-1.5`} />
          </div>
        </div>

        <button onClick={save} disabled={busy} data-testid="save-video" className="mt-2 w-full bg-[#FFD60A] text-black py-3 font-semibold hover:bg-[#FFD60A]/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save changes"}
        </button>

        <div className="border-t border-white/10 mt-2 pt-3">
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)} data-testid="delete-video-btn" className="w-full text-red-400 hover:text-red-300 text-sm flex items-center justify-center gap-2 py-2">
              <Trash2 className="h-4 w-4" /> Delete this video
            </button>
          ) : (
            <div className="bg-red-500/10 border border-red-500/30 p-3">
              <p className="text-sm text-red-300 flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4" /> Delete "{video.title}"? This can't be undone.</p>
              <div className="flex gap-2">
                <button onClick={doDelete} disabled={busy} data-testid="confirm-delete" className="flex-1 bg-red-500 text-white py-2 text-sm font-semibold hover:bg-red-600 transition-colors">Yes, delete</button>
                <button onClick={() => setConfirmDelete(false)} className="flex-1 bg-white/10 text-white py-2 text-sm hover:bg-white/20 transition-colors">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
